import { useAEMComponentData } from "@AEM/AEMContentLoader"
import { AEMComponentMapper, AEMPageType } from "@AEM/models/AEMPage.model"
import { AEMImage, AEMImageData } from "@components/AEMImage"
import { Button, Stack, Text } from "@mantine/core"
import { clsx } from "@mantine/styles"
import { Brand, Brands } from "@utils/constants.util"
import { FC, useEffect, useState } from "react"
import classes from "./BrandSwitch.module.css"

export interface BrandButton {
	brandCode: Brand
	brandName: string
	icon: AEMImageData
}

interface BrandSwitchProps {
	onSwitch: (brand: Brand) => void
	initValue?: Brand
	small?: boolean
	title?: string
	availableBrands?: Brand[]
}

const defaultBrands: Brand[] = [
	Brands.ROYAL,
	Brands.CELEBRITY,
	Brands.SILVERSEA
]

const getButtonClasses = (brand: Brand, isActive: boolean) => {
	return clsx(classes.button, classes[brand ?? ""], isActive && classes.active)
}

export const BrandSwitch: FC<BrandSwitchProps> = ({
	initValue = null,
	availableBrands = defaultBrands,
	small = false,
	title,
	onSwitch
}) => {
	const { componentData } = useAEMComponentData(
		AEMPageType.PAGES_PROMOTIONS_SEARCH_RESULTS,
		AEMComponentMapper.PROMOTIONS_BRAND
	)
	const [activeBrand, setActiveBrand] = useState<Brand | null>(initValue)

	useEffect(() => {
		setActiveBrand(initValue)
	}, [initValue])

	const handleSwitch = (brand: Brand) => {
		setActiveBrand(brand)
		onSwitch(brand)
	}
	const brands = availableBrands.map((availableBrand) => {
		const brandData = componentData?.brands.find(
			(b: BrandButton) => b.brandCode === availableBrand
		)
		return {
			brandCode: availableBrand,
			brandName: brandData?.brandName,
			icon: brandData?.icon
		}
	})

	return (
		<Stack align="center" gap="xs">
			{title && (
				<Text fw={600} size="sm">
					{title}
				</Text>
			)}
			<Button.Group className={clsx(classes.group, small && classes.small)}>
				{brands.map(({ brandCode, icon }: BrandButton) => (
					<Button
						key={brandCode}
						variant="default"
						className={getButtonClasses(brandCode, activeBrand === brandCode)}
						onClick={() => handleSwitch(brandCode)}
					>
						{icon && (
							<AEMImage
								w={"auto"}
								h={28}
								className={
									activeBrand === brandCode
										? classes.iconActive
										: classes.iconInactive
								}
								imageData={{ ...icon }}
							/>
						)}
					</Button>
				))}
			</Button.Group>
		</Stack>
	)
}
